import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { apiFetch } from '../api'

type Severity = 'P1' | 'P2' | 'P3'

interface VelocityAnomalyEvidence {
  price_change_rub?: number | null
  spp_change_rub?: number | null
  competitor_min_spp_rub?: number | null
  competitor_undercut?: boolean
  moscow_stock_days?: number | null
  cluster_oos_count?: number
  scraper_stale_hours?: number | null
  badge_lost?: boolean
}

interface VelocityAnomaly {
  id: number
  severity: Severity | null
  created_at: string | null
  status: string | null
  sku: string | null
  product_name: string | null
  recent_velocity: number | null
  baseline_velocity: number | null
  drop_ratio_pct: number | null
  evidence: VelocityAnomalyEvidence
  recommendations: string[]
}

interface AnomaliesResponse {
  status: string
  anomalies: VelocityAnomaly[]
}

interface VelocityAnomalyBannerProps {
  /** Polling interval in ms. Defaults to 5 minutes. */
  pollIntervalMs?: number
  /** Severity filter. Defaults to all. */
  severityFilter?: Severity | null
  /** Lookback window in hours. Defaults to 48. */
  hours?: number
}

const SEVERITY_ORDER: Record<Severity, number> = { P1: 0, P2: 1, P3: 2 }

function severityClass(s: Severity | null): string {
  if (s === 'P1') return 'border-rose-300 bg-rose-50 text-rose-900'
  if (s === 'P2') return 'border-amber-300 bg-amber-50 text-amber-900'
  return 'border-yellow-200 bg-yellow-50 text-yellow-900'
}

function severityBadgeClass(s: Severity | null): string {
  if (s === 'P1') return 'bg-rose-600 text-white'
  if (s === 'P2') return 'bg-amber-500 text-white'
  return 'bg-yellow-400 text-yellow-900'
}

function severityLabel(s: Severity | null): string {
  if (s === 'P1') return 'Критическое'
  if (s === 'P2') return 'Внимание'
  if (s === 'P3') return 'Наблюдение'
  return ''
}

function formatRelative(iso: string | null): string {
  if (!iso) return ''
  const past = new Date(iso)
  if (Number.isNaN(past.getTime())) return ''
  const diffMs = Date.now() - past.getTime()
  const minutes = Math.max(0, Math.round(diffMs / 60000))
  if (minutes < 60) return `${minutes} мин назад`
  const hours = Math.round(minutes / 60)
  if (hours < 48) return `${hours} ч назад`
  const days = Math.round(hours / 24)
  return `${days} дн назад`
}

function formatVelocity(value: number | null | undefined): string {
  if (value == null) return '—'
  return `${value.toFixed(1)}/день`
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message
  return 'Не удалось загрузить алерты'
}

export function VelocityAnomalyBanner({
  pollIntervalMs = 5 * 60 * 1000,
  severityFilter = null,
  hours = 48,
}: VelocityAnomalyBannerProps) {
  const [anomalies, setAnomalies] = useState<VelocityAnomaly[]>([])
  const [expandedId, setExpandedId] = useState<number | null>(null)
  const [busyId, setBusyId] = useState<number | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [dismissedIds, setDismissedIds] = useState<Set<number>>(new Set())

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams()
      params.set('hours', String(hours))
      if (severityFilter) params.set('severity', severityFilter)
      // Роутер смонтирован с префиксом /api (main.py: AliasRouter(prefix='/api')).
      // Без него запрос уходил в SPA-фолбэк и баннер не отрисовывался ни разу
      // с момента появления фичи.
      const response = await apiFetch(`/api/ai/velocity-anomalies?${params.toString()}`)
      const body: AnomaliesResponse = await response.json()
      const next = Array.isArray(body?.anomalies) ? body.anomalies : []
      setAnomalies(next)
      setErrorMessage(null)
    } catch (error: unknown) {
      setErrorMessage(getErrorMessage(error))
    }
  }, [hours, severityFilter])

  useEffect(() => {
    load().catch(() => undefined)
    if (pollIntervalMs <= 0) return undefined
    const handle = window.setInterval(() => {
      load().catch(() => undefined)
    }, pollIntervalMs)
    return () => window.clearInterval(handle)
  }, [load, pollIntervalMs])

  const visibleAnomalies = useMemo(
    () =>
      anomalies
        .filter(a => a.severity != null)
        .filter(a => !dismissedIds.has(a.id))
        .sort((a, b) => {
          const sa = SEVERITY_ORDER[a.severity as Severity] ?? 99
          const sb = SEVERITY_ORDER[b.severity as Severity] ?? 99
          if (sa !== sb) return sa - sb
          // newer first within same severity
          const ta = a.created_at ? Date.parse(a.created_at) : 0
          const tb = b.created_at ? Date.parse(b.created_at) : 0
          return tb - ta
        }),
    [anomalies, dismissedIds],
  )

  const counts = useMemo(() => {
    const out: Record<Severity, number> = { P1: 0, P2: 0, P3: 0 }
    for (const a of visibleAnomalies) {
      if (a.severity) out[a.severity] += 1
    }
    return out
  }, [visibleAnomalies])

  const totalCount = visibleAnomalies.length

  const handleDismissLocally = useCallback((id: number) => {
    setBusyId(id)
    setDismissedIds(prev => {
      const next = new Set(prev)
      next.add(id)
      return next
    })
    setBusyId(null)
  }, [])

  if (totalCount === 0) {
    return null
  }

  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div>
            <div className="font-semibold text-slate-900">
              ⚠️ Аномалии velocity ({totalCount})
            </div>
            <div className="text-xs text-slate-600">
              {counts.P1 > 0 && (
                <span className="mr-3 font-semibold text-rose-700">
                  {counts.P1} критических
                </span>
              )}
              {counts.P2 > 0 && (
                <span className="mr-3 font-semibold text-amber-700">
                  {counts.P2} внимание
                </span>
              )}
              {counts.P3 > 0 && (
                <span className="text-yellow-700">{counts.P3} наблюдение</span>
              )}
            </div>
          </div>
        </div>
        {errorMessage && (
          <div className="text-xs text-rose-600">{errorMessage}</div>
        )}
      </div>

      <div className="mt-3 space-y-2">
        {visibleAnomalies.slice(0, 5).map(anomaly => {
          const isExpanded = expandedId === anomaly.id
          return (
            <div
              key={anomaly.id}
              className={`rounded-xl border px-3 py-2 text-sm ${severityClass(anomaly.severity)}`}
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className={`rounded px-2 py-0.5 text-[11px] font-bold ${severityBadgeClass(anomaly.severity)}`}
                    title={severityLabel(anomaly.severity)}
                  >
                    {anomaly.severity}
                  </span>
                  <span className="font-semibold">
                    {anomaly.product_name || anomaly.sku}
                  </span>
                  <span className="text-[11px] opacity-75">
                    SKU {anomaly.sku}
                  </span>
                  <span className="text-[11px] opacity-75">
                    • {formatRelative(anomaly.created_at)}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      setExpandedId(isExpanded ? null : anomaly.id)
                    }
                    className="rounded-lg border border-current bg-white/40 px-2 py-1 text-[11px] font-medium hover:bg-white/70"
                  >
                    {isExpanded ? 'Свернуть' : 'Подробнее'}
                  </button>
                  <button
                    type="button"
                    disabled={busyId === anomaly.id}
                    onClick={() => handleDismissLocally(anomaly.id)}
                    className="rounded-lg border border-slate-300 bg-white/60 px-2 py-1 text-[11px] font-medium text-slate-700 hover:bg-white disabled:opacity-50"
                    title="Скрыть до следующей загрузки"
                  >
                    Скрыть
                  </button>
                </div>
              </div>

              <div className="mt-2 flex flex-wrap items-center gap-3 text-xs">
                <span>
                  velocity:{' '}
                  <strong>{formatVelocity(anomaly.recent_velocity)}</strong>
                </span>
                <span className="opacity-75">
                  baseline: {formatVelocity(anomaly.baseline_velocity)}
                </span>
                <span className="font-semibold">
                  падение: {anomaly.drop_ratio_pct?.toFixed(1)}%
                </span>
              </div>

              {isExpanded && (
                <div className="mt-3 space-y-2 border-t border-current/30 pt-2">
                  {anomaly.recommendations.length > 0 && (
                    <div>
                      <div className="text-[11px] font-semibold uppercase opacity-70">
                        Рекомендации
                      </div>
                      <ul className="mt-1 space-y-1 text-xs">
                        {anomaly.recommendations.map((rec, idx) => (
                          <li key={idx}>{rec}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div>
                    <div className="text-[11px] font-semibold uppercase opacity-70">
                      Что обнаружено
                    </div>
                    <ul className="mt-1 space-y-0.5 text-xs">
                      {anomaly.evidence.price_change_rub != null &&
                        anomaly.evidence.price_change_rub !== 0 && (
                          <li>
                            Цена изменилась на{' '}
                            <strong>
                              {anomaly.evidence.price_change_rub > 0 ? '+' : ''}
                              {anomaly.evidence.price_change_rub.toFixed(0)} ₽
                            </strong>
                          </li>
                        )}
                      {anomaly.evidence.spp_change_rub != null &&
                        anomaly.evidence.spp_change_rub !== 0 && (
                          <li>
                            SPP сдвинулась на{' '}
                            <strong>
                              {anomaly.evidence.spp_change_rub > 0 ? '+' : ''}
                              {anomaly.evidence.spp_change_rub.toFixed(0)} ₽
                            </strong>
                          </li>
                        )}
                      {anomaly.evidence.competitor_undercut &&
                        anomaly.evidence.competitor_min_spp_rub != null && (
                          <li>
                            Конкурент с SPP{' '}
                            <strong>
                              {anomaly.evidence.competitor_min_spp_rub.toFixed(
                                0,
                              )}{' '}
                              ₽
                            </strong>{' '}
                            (мы дороже)
                          </li>
                        )}
                      {anomaly.evidence.moscow_stock_days != null &&
                        anomaly.evidence.moscow_stock_days < 14 && (
                          <li>
                            Москва-сток на{' '}
                            <strong>
                              {anomaly.evidence.moscow_stock_days.toFixed(1)} дн.
                            </strong>
                          </li>
                        )}
                      {anomaly.evidence.cluster_oos_count != null &&
                        anomaly.evidence.cluster_oos_count > 0 && (
                          <li>
                            OOS на{' '}
                            <strong>
                              {anomaly.evidence.cluster_oos_count}
                            </strong>{' '}
                            кластерах
                          </li>
                        )}
                      {anomaly.evidence.scraper_stale_hours != null && (
                        <li>
                          Scraper не обновляет SPP{' '}
                          <strong>
                            {anomaly.evidence.scraper_stale_hours.toFixed(0)} ч
                          </strong>
                        </li>
                      )}
                      {anomaly.evidence.badge_lost && (
                        <li>
                          Возможно потерян badge «выгодная цена» (конкуренты
                          близко)
                        </li>
                      )}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          )
        })}

        {visibleAnomalies.length > 5 && (
          <div className="text-xs text-slate-500">
            …и ещё {visibleAnomalies.length - 5} (скрыто). Используйте фильтры
            для уточнения списка.
          </div>
        )}
      </div>
    </div>
  )
}

export default VelocityAnomalyBanner
